import {
  ConsumableCadence,
  MonitorIoTClass,
  MonitorType,
} from "@/services/backend/qpm/types/enums";
import type { MonitorAdditionalReq } from "@/services/backend/qpm/monitors/types/monitor-commands";
import type {
  CapabilitiesDTO,
  DetailsDTO,
  MonitorSnapDTO,
  PestSnapDTO,
} from "@/services/backend/qpm/monitors/types/monitor-snapshot";
import { DiffCategory } from "@/services/backend/qpm/catalog/types/catalog-enum";
import type { OrdrBy } from "@/shared/lib/ordr/ordrBy";
import type { Filter } from "@/shared/lib/filter/filter";

export interface Catalog {
  id: string;
  name: string;
  description: string;
  customerId?: string;
  createdAt: string;
  updatedAt: string;
}

export interface CatalogListItem {
  id: string;
  name: string;
  description: string;
  customerId?: string;
  included: number;
  total: number;
  createdAt: string;
  updatedAt: string;
}

export interface CatalogList {
  items: CatalogListItem[];
  total: number;
  count: number;
  limit: number;
  offset: number;
  index: number;
}

export interface CreateCatalogRequest {
  companyId: string;
  name: string;
  description: string;
  customerId?: string;
}

export interface GetCatalogRequest {
  id: string;
}

export interface DeleteCatalogRequest {
  id: string;
}

export interface CopyCatalogItemsRequest {
  sourceCatalogId: string;
  targetCatalogId: string;
}

export interface CopyCatalogItemsResponse {
  sourceCatalogId: string;
  targetCatalogId: string;
  copiedItems: number;
}

export interface UpdateDescriptionReq {
  id: string;
  description: string;
}

export interface ListRequest {
  company: Filter<string>;
  name?: Filter<string>;
  customer?: Filter<string>;
  orderBy?: OrdrBy<"name" | "createdAt" | "updatedAt">[];
  limit?: number;
  offset?: number;
}

export interface IncludeMonitorRequest {
  catalogId: string;
  monitorId: string;
  strict: boolean;
}

export interface IncludeMonitorResponse {
  catalogId: string;
  monitorId: string;
}

export interface ExcludeMonitorRequest {
  catalogId: string;
  monitorId: string;
}

export interface ExcludeMonitorResponse {
  catalogId: string;
  monitorId: string;
}

export interface PriceReq {
  euros: number;
  effectiveOn: string;
}

export interface VisitsReq {
  perYear: number;
}

export interface ConsumableOverrideReq {
  consumable: MonitorSnapDTO["consumables"][number]["consumable"]; // shared.ConsumableSnapDTO

  quantityPerDevice?: number;
  cadence?: ConsumableCadence;
  customAmountOfMeasures?: number;

  // Legacy only
  perDevice?: number;
  perYear?: number;

  price?: PriceReq;
  note?: string;
}

export interface RentingFeeReq {
  durationMonths: number;
  /** unit.Euros as number */
  euros: number;
  effectiveOn: string;
  markedForDelete?: boolean;
}

export interface RentingOverrideReq {
  fees: RentingFeeReq[];
}

export interface ActivateRequest {
  catalogId: string;
  customerId?: string;
  releaseId?: string;
  /** Backend field name: effectiveFrom (json tag: activeSince) */
  effectiveFrom?: string;
}

export interface ActiveCatalog {
  id: string;
  name: string;
  description: string;
  customerId?: string;
  createdAt: string;
  updatedAt: string;
  activeSince: string;
}

export interface RentingMatrix {
  [monitorId: string]: {
    [durationMonths: number]: number;
  };
}

export interface ReleaseDTO {
  id: string;
  catalogId: string;
  name: string;
  customerId?: string;
  publishedAt: string;
  renting: RentingMatrix;
  monitors: MonitorSnapDTO[];
}

export interface ActiveCatalogRelease {
  catalog: Catalog;
  release: ReleaseDTO;
}

// Monitor list view (backend /catalog/monitors)
export interface ListMonitorsRequest {
  // Backend expects base64 JSON query param "request"; keep generic shape here.
  catalog?: unknown;
  type?: unknown;
  limit?: number;
  offset?: number;
}

export interface ListMonitorItemRes {
  catalogItemId: string;
  baseMonitorId: string;

  id: string;
  name: string;
  type: string;
  supplier: string;
}

export interface ListMonitorViewRes {
  items: ListMonitorItemRes[];
}

// Catalog items endpoints
export interface GetCatalogItemRequest {
  catalogItemId: string;
}

export interface GetCatalogItemByBaseRequest {
  catalogId: string;
  baseMonitorId: string;
}

export interface CatalogItemRes {
  catalogItemId: string;
  catalogId: string;
  baseMonitorId: string;
  included: boolean;
  snapshot: MonitorSnapDTO;
}

export interface SetItemCoreFieldsReq {
  catalogItemId: string;
  name: string;
  supplier: string;
}

export interface SetItemCapabilitiesReq {
  catalogItemId: string;
  capabilities: CapabilitiesDTO;
}

export interface SetItemDetailsReq {
  catalogItemId: string;
  details: DetailsDTO;
}

export interface SetItemPestsReq {
  catalogItemId: string;
  pests: PestSnapDTO[];
}

export interface SetItemImageReq {
  catalogItemId: string;
  fileId: string;
  file: File | null;
}

export interface SetItemBasePriceReq {
  catalogItemId: string;
  /** unit.Euros as number */
  euros: number;
  effectiveOn: string;
}

export interface SetItemRentingReq {
  catalogItemId: string;
  fees: {
    durationMonths: number;
    /** unit.Euros as number */
    monthly: number;
  }[];
}

export interface SetItemConsumablesReq {
  catalogItemId: string;
  consumables: ConsumableOverrideReq[];
}

export interface SetItemAdditionalsReq {
  catalogItemId: string;
  additionals: MonitorAdditionalReq[];
}

export interface SetItemAdditionalImageReq {
  catalogItemId: string;
  additionalId: string;
  fileId: string;
  file: File | null;
}

export interface DeleteItemAdditionalImageReq {
  catalogItemId: string;
  additionalId: string;
}

export interface SetItemRecommendedVisitsReq {
  catalogItemId: string;
  perYear: number;
}

export interface SetItemServiceTimesReq {
  catalogItemId: string;
  install: number;
  maintain: number;
}

export interface SetItemIncludeInCheckpointServiceReq {
  catalogItemId: string;
  includeInCheckpointService: boolean;
}

export interface DeleteItemImageReq {
  catalogItemId: string;
}

export interface ListCatalogItemsRequest {
  catalogId: string;
  includedOnly: boolean;
  limit?: number;
  offset?: number;
}

export interface ListCatalogItemsResponse {
  items: CatalogItemRes[];
}

// Legacy (pre-v2) endpoints/types kept for compatibility; backend no longer exposes them.
/** @deprecated backend replaced by /catalog/items/* endpoints */
export interface SetOverridesRequest {
  catalogId: string;
  monitorId: string;
  basePrice?: PriceReq;
  visits?: VisitsReq;
  consumables?: ConsumableOverrideReq[];
  renting?: RentingOverrideReq;
}

/** @deprecated backend replaced by /catalog/items/renting */
export interface SetCatalogMonitorRentingReq {
  catalogId: string;
  monitorId: string;
  fees: {
    durationMonths: number;
    /** unit.Euros as number */
    monthly: number;
  }[];
}

export interface PublishRequest {
  catalogId: string;
}

export interface GetReleaseRequest {
  releaseId: string;
}

export interface GetActiveReleaseRequest {
  customerId?: string;
  at?: string;
}

export interface ResolveActiveRequest {
  customerId?: string;
  at?: string;
}

export interface LatestReleaseRequest {
  catalogId: string;
}

export interface CreateMonitorRequest {
  catalogId: string;
  monitorType: MonitorType;
  name: string;
  /** time.Duration */
  install: number;
  /** time.Duration */
  maintain: number;
  /** Only required for IoT monitors */
  iotClass?: MonitorIoTClass;
  /** Only required for IoT monitors */
  iotProtocol?: string;
}

export interface CreateMonitorResponse {
  monitorId: string;
}

export interface RefreshOptionsReq {
  force: boolean;
  overrideCoreFields: boolean;
  overrideCapabilities: boolean;
  overrideQPMService: boolean;
  overrideDetails: boolean;
  overrideImage: boolean;
  overridePests: boolean;
  overrideTimes: boolean;
  overrideRecommendedVisits: boolean;
  overrideBasePrice: boolean;
  overrideAdditionals: boolean;
  overrideRentingDefaults: boolean;
  overrideConsumables: boolean;
  overrideConsumablePrices: boolean;
}

export interface RefreshSingleReq {
  releaseId: string;
  monitorId: string;
  catalogId: string;
  options: RefreshOptionsReq;
}

export interface RefreshAllReq {
  releaseId: string;
  catalogId: string;
  options: RefreshOptionsReq;
}

export interface RefreshMonitorRes {
  monitorId: string;
  changed: boolean;
  oldHash: string;
  newHash: string;
  changedCategories: DiffCategory[];
  proposedSnapshot?: MonitorSnapDTO;
}

export interface RefreshAllRes {
  results: RefreshMonitorRes[];
}

export const DEFAULT_REFRESH_OPTIONS: RefreshOptionsReq = {
  force: false,
  overrideCoreFields: true,
  overrideCapabilities: true,
  overrideQPMService: true,
  overrideDetails: true,
  overrideImage: true,
  overridePests: true,
  overrideTimes: false,
  overrideRecommendedVisits: false,
  overrideBasePrice: false,
  overrideAdditionals: true,
  overrideRentingDefaults: false,
  overrideConsumables: false,
  overrideConsumablePrices: false,
};
